import { useCallback, useRef, useEffect } from "react";
import { defaultHeaderContentHeight } from "@/store/pageModules/header";
import useOptimizedScroll from "@/src/hooks/useOptimizedScroll";

interface UseHeaderScrollBehaviorProps {
    headerRef: React.RefObject<HTMLDivElement>;
    headerContentHeight: number | null;
    hiddenWhenNoFixed?: boolean;
    anouncementHeaderHeight: number;
    setHeaderOptions: (options: Array<Partial<any>> | Partial<any>) => void;
}

export const useHeaderScrollBehavior = ({
    headerRef,
    headerContentHeight,
    hiddenWhenNoFixed,
    anouncementHeaderHeight,
    setHeaderOptions,
}: UseHeaderScrollBehaviorProps) => {
    // 滚动方向: wheel > 0 ？ 向上 ： 向下
    const lastScrollTop = useRef(0);

    // 当滚动高度超过顶部导航栏距离顶部高度时=>fixed
    const handleScroll = useCallback(() => {
        if (headerRef.current) {
            // 获取当前滚动位置和方向
            const scrollTop = document.documentElement.scrollTop;
            const isScrollingDown = scrollTop > lastScrollTop.current;

            // 提取常用计算值
            const headerBottomPosition =
                headerRef.current.getBoundingClientRect().bottom;
            const headerHeight =
                headerContentHeight ?? defaultHeaderContentHeight;

            // 判断是否应该固定header的内联函数
            const shouldFixHeaderInHiddenMode = () => {
                if (isScrollingDown) {
                    // 向下滚动：header完全离开视窗时才固定
                    return headerBottomPosition < 0;
                } else {
                    // 向上滚动：header底部小于header高度时就固定
                    return headerBottomPosition < headerHeight;
                }
            };

            const shouldFixHeaderInNormalMode = () => {
                // 普通模式：header底部小于header高度时就固定
                return headerBottomPosition < headerHeight;
            };

            // 根据配置决定使用哪种判断逻辑
            let shouldFixHeader: boolean;
            if (hiddenWhenNoFixed) {
                // 隐藏模式：根据滚动方向采用不同策略
                shouldFixHeader = shouldFixHeaderInHiddenMode();
            } else {
                // 普通模式：统一判断逻辑
                shouldFixHeader = shouldFixHeaderInNormalMode();
            }

            // 更新header固定状态
            setHeaderOptions([{ fixedHeader: shouldFixHeader }]);

            // 更新滚动位置记录
            lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
        }
    }, [headerContentHeight, hiddenWhenNoFixed, setHeaderOptions, headerRef]);

    // 初始化header固定状态
    useEffect(() => {
        // 首次渲染，scrollY的位置决定header是否存在
        setHeaderOptions([
            {
                fixedHeader: window.scrollY > anouncementHeaderHeight,
            },
        ]);
    }, [anouncementHeaderHeight, setHeaderOptions]);

    // 绑定滚动监听
    useOptimizedScroll({ onScroll: handleScroll });
};
